import React, { useEffect, useState } from "react";
import { Form, Input, DatePicker, Button, Row, Col, Card, Select, InputNumber, Typography, Flex, Tabs, Tag, Popconfirm, Upload, Image } from 'antd';
import Table from 'components/Common/Table';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarcodeOutlined, PrinterOutlined, UploadOutlined, StarOutlined, StarFilled, CheckCircleOutlined } from "@ant-design/icons";
import * as actions from "store/actions/AuthActions";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useNotification } from 'NotificationContext';
import dayjs from 'dayjs';
import AssetAssignmentForm from 'components/Assets/AssetAssignmentForm';
import AssetMaintenanceForm from 'components/Assets/AssetMaintenanceForm';

const { TextArea } = Input;
const { Option } = Select;

const AssetForm = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const notify = useNotification();

    // Get barcode from URL params (from scanner)
    const barcodeFromScanner = searchParams.get('barcode');

    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [maintenanceRecords, setMaintenanceRecords] = useState([]);
    const [history, setHistory] = useState([]);
    const [barcodeImage, setBarcodeImage] = useState(null);
    const [qrcodeImage, setQrcodeImage] = useState(null);
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [pendingImages, setPendingImages] = useState([]); // Images queued for upload after asset creation

    // Document Category states
    const [assetDocCategoryId, setAssetDocCategoryId] = useState(null);
    const [assetFileDocCategoryId, setAssetFileDocCategoryId] = useState(null);

    // Drawer states
    const [assignmentDrawerVisible, setAssignmentDrawerVisible] = useState(false);
    const [maintenanceDrawerVisible, setMaintenanceDrawerVisible] = useState(false);
    const [currentAssignment, setCurrentAssignment] = useState(null);
    const [currentMaintenance, setCurrentMaintenance] = useState(null);


    const loadAsset = async () => {
        if (id) {
            setLoading(true);
            try {
                const response = await actions.retrieveAsset(id);
                if (response) {
                    const assetData = { ...response };
                    // Convert date fields
                    const dateFields = ['purchase_date', 'warranty_expiry', 'assigned_date'];
                    dateFields.forEach(field => {
                        if (assetData[field]) {
                            assetData[field] = dayjs(assetData[field]);
                        }
                    });
                    form.setFieldsValue(assetData);
                    setAssignments(response.assignments || []);
                    setMaintenanceRecords(response.maintenance_records || []);
                    setBarcodeImage(response.barcode_image_url);
                    setQrcodeImage(response.qrcode_image_url);
                    setImages(response.images || []);

                    // Load history
                    loadHistory();
                }
            } catch (error) {
                notify("error", "Failed to load asset");
            }
            setLoading(false);
        }
    };

    const loadHistory = async () => {
        if (id) {
            try {
                const response = await actions.loadAssetHistory(id);
                if (response && response.status === 200) {
                    setHistory(response.data || []);
                }
            } catch (error) {
                console.error("Failed to load history", error);
            }
        }
    };

    const loadCategories = async () => {
        try {
            const response = await actions.loadAssetCategories();
            if (response && response.status === 200) {
                setCategories(response.data.results || response.data || []);
            }
        } catch (error) {
            console.error("Failed to load categories", error);
        }
    };

    const loadLocations = async () => {
        try {
            const response = await actions.loadAssetLocations();
            if (response && response.status === 200) {
                setLocations(response.data.results || response.data || []);
            }
        } catch (error) {
            console.error("Failed to load locations", error);
        }
    };

    const loadEmployees = async () => {
        try {
            const response = await actions.loadEmployees();
            if (response && response.results) {
                setEmployees(response.results);
            }
        } catch (error) {
            console.error("Failed to load employees", error);
        }
    };

    useEffect(() => {
        loadCategories();
        loadLocations();
        loadEmployees();

        // Fetch document categories
        const fetchDocCategories = async () => {
            const assetsCatId = await actions.getDocumentCategoryByCode('ASSETS');
            const assetFileCatId = await actions.getDocumentCategoryByCode('ASSETFILE');
            setAssetDocCategoryId(assetsCatId);
            setAssetFileDocCategoryId(assetFileCatId);
        };
        fetchDocCategories();

        if (id) {
            loadAsset();
        } else if (barcodeFromScanner) {
            // Pre-fill barcode from scanner when creating new asset
            form.setFieldsValue({ barcode: barcodeFromScanner });
        }
    }, [id, barcodeFromScanner]);

    const saveAsset = async (values, saveAndNew = false) => {
        setLoading(true);

        // Format dates
        const formattedValues = { ...values };
        const dateFields = ['purchase_date', 'warranty_expiry', 'assigned_date'];
        dateFields.forEach(field => {
            if (formattedValues[field]) {
                formattedValues[field] = dayjs(formattedValues[field]).format('YYYY-MM-DD');
            }
        });

        // Add document_category_id if available
        if (assetDocCategoryId) {
            formattedValues.document_category_id = assetDocCategoryId;
        }

        try {
            if (id) {
                const response = await actions.updateAsset(formattedValues, id);
                if (response) {
                    notify("success", "Asset updated successfully");
                    if (saveAndNew) {
                        navigate('/assets/new');
                        form.resetFields();
                    }
                }
            } else {
                const response = await actions.addAsset(formattedValues);
                if (response && response.status === 201) {
                    notify("success", "Asset created successfully");
                    const newAssetId = response.data.id;

                    // Upload pending images if any
                    if (pendingImages.length > 0) {
                        await uploadPendingImages(newAssetId);
                    }

                    if (saveAndNew) {
                        form.resetFields();
                        setPendingImages([]);
                    } else {
                        navigate('/assets/edit/' + newAssetId);
                    }
                }
            }
        } catch (error) {
            notify("error", "Failed to save asset");
        }
        setLoading(false);
    };

    const handleGenerateBarcode = async () => {
        if (!id) {
            notify("warning", "Please save the asset first");
            return;
        }
        try {
            const response = await actions.generateAssetBarcode(id);
            if (response && response.status === 200) {
                notify("success", response.data.message || "Barcode & QR Code generated successfully");
                setBarcodeImage(response.data.asset.barcode_image_url);
                setQrcodeImage(response.data.asset.qrcode_image_url);
                form.setFieldsValue({ barcode: response.data.asset.barcode });
            }
        } catch (error) {
            notify("error", "Failed to generate barcode & QR code");
        }
    };

    const handlePrintBarcode = () => {
        if (!barcodeImage && !qrcodeImage) {
            notify("warning", "Please generate barcode & QR code first");
            return;
        }
        const assetName = form.getFieldValue('name');
        const assetNumber = form.getFieldValue('asset_number');

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Print Barcode & QR Code - ${assetNumber}</title>
                <style>
                    @media print {
                        @page { size: 80mm 50mm; margin: 2mm; }
                        body { margin: 0; padding: 0; }
                        .no-print { display: none !important; }
                    }
                    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                    .label-container {
                        width: 76mm; height: 46mm; padding: 2mm;
                        border: 1px solid #ccc;
                        background: white;
                    }
                    .company-name { font-size: 10px; font-weight: bold; text-align: center; margin-bottom: 2mm; }
                    .codes-row {
                        display: flex; align-items: center;
                        justify-content: space-between; gap: 3mm;
                    }
                    .barcode-section { flex: 1; text-align: center; }
                    .qrcode-section { width: 22mm; text-align: center; }
                    .barcode-img { max-width: 48mm; height: auto; }
                    .qrcode-img { width: 20mm; height: 20mm; }
                    .asset-info { font-size: 8px; text-align: center; margin-top: 2mm; color: #666; }
                    .print-btn {
                        display: block; margin: 20px auto;
                        padding: 10px 30px; font-size: 16px;
                        background: #28a745; color: white;
                        border: none; border-radius: 5px; cursor: pointer;
                    }
                </style>
            </head>
            <body>
                <button class="print-btn no-print" onclick="window.print()">Print Barcode & QR Code</button>
                <div class="label-container">
                    <div class="company-name">TESON LLP</div>
                    <div class="codes-row">
                        <div class="barcode-section">
                            ${barcodeImage ? `<img src="${barcodeImage}" class="barcode-img" alt="Barcode" />` : '<p>No barcode</p>'}
                        </div>
                        <div class="qrcode-section">
                            ${qrcodeImage ? `<img src="${qrcodeImage}" class="qrcode-img" alt="QR Code" />` : '<p>No QR</p>'}
                        </div>
                    </div>
                    <div class="asset-info">${assetName} | ${assetNumber}</div>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleDeleteAssignment = async (assignmentId) => {
        try {
            const response = await actions.deleteAssetAssignment(assignmentId);
            if (response) {
                notify("success", "Assignment deleted successfully");
                loadAsset();
            }
        } catch (error) {
            notify("error", "Failed to delete assignment");
        }
    };

    const handleDeleteMaintenance = async (maintenanceId) => {
        try {
            const response = await actions.deleteAssetMaintenance(maintenanceId);
            if (response) {
                notify("success", "Maintenance record deleted successfully");
                loadAsset();
            }
        } catch (error) {
            notify("error", "Failed to delete maintenance record");
        }
    };

    // Image handling
    const handleImageUpload = async (file) => {
        if (!id) {
            // Queue image for upload after asset creation
            const preview = URL.createObjectURL(file);
            setPendingImages(prev => [...prev, {
                file,
                preview,
                name: file.name,
                uid: `pending-${Date.now()}-${Math.random()}`
            }]);
            notify("info", "Image queued. It will be uploaded when you save the asset.");
            return false;
        }

        setUploading(true);
        try {
            const response = await actions.uploadAssetImage(id, file, '', false, 0, assetFileDocCategoryId);
            if (response && response.status === 201) {
                notify("success", "Image uploaded successfully");
                loadAsset();
            }
        } catch (error) {
            notify("error", "Failed to upload image");
        }
        setUploading(false);
        return false; // Prevent default upload behavior
    };

    // Remove pending image
    const handleRemovePendingImage = (uid) => {
        setPendingImages(prev => {
            const imageToRemove = prev.find(img => img.uid === uid);
            if (imageToRemove && imageToRemove.preview) {
                URL.revokeObjectURL(imageToRemove.preview);
            }
            return prev.filter(img => img.uid !== uid);
        });
    };

    // Upload all pending images after asset creation
    const uploadPendingImages = async (assetId) => {
        if (pendingImages.length === 0) return;

        setUploading(true);
        let successCount = 0;
        let failCount = 0;

        for (const pendingImage of pendingImages) {
            try {
                const response = await actions.uploadAssetImage(assetId, pendingImage.file, '', false, 0, assetFileDocCategoryId);
                if (response && response.status === 201) {
                    successCount++;
                }
            } catch (error) {
                console.error("Failed to upload image:", error);
                failCount++;
            }
            // Revoke the object URL
            if (pendingImage.preview) {
                URL.revokeObjectURL(pendingImage.preview);
            }
        }

        setPendingImages([]);
        setUploading(false);

        if (successCount > 0) {
            notify("success", `${successCount} image(s) uploaded successfully`);
        }
        if (failCount > 0) {
            notify("error", `${failCount} image(s) failed to upload`);
        }
    };

    const handleDeleteImage = async (imageId) => {
        try {
            const response = await actions.deleteAssetImage(imageId);
            if (response) {
                notify("success", "Image deleted successfully");
                loadAsset();
            }
        } catch (error) {
            notify("error", "Failed to delete image");
        }
    };

    const handleSetPrimaryImage = async (imageId) => {
        try {
            const response = await actions.setAssetImagePrimary(imageId);
            if (response && response.status === 200) {
                notify("success", "Primary image set successfully");
                loadAsset();
            }
        } catch (error) {
            notify("error", "Failed to set primary image");
        }
    };

    const assignmentColumns = [
        {
            title: 'Employee',
            dataIndex: 'employee_name',
            key: 'employee_name',
        },
        {
            title: 'Assigned Date',
            dataIndex: 'assigned_date',
            key: 'assigned_date',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Returned Date',
            dataIndex: 'returned_date',
            key: 'returned_date',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Type',
            dataIndex: 'assignment_type_display',
            key: 'assignment_type_display',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Tag color={record.is_active ? 'green' : 'default'}>
                    {record.is_active ? 'Active' : 'Returned'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Flex gap="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setCurrentAssignment(record);
                            setAssignmentDrawerVisible(true);
                        }}
                    />
                    <Popconfirm
                        title="Delete this assignment?"
                        onConfirm={() => handleDeleteAssignment(record.id)}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Flex>
            )
        }
    ];

    const maintenanceColumns = [
        {
            title: 'Type',
            dataIndex: 'maintenance_type_display',
            key: 'maintenance_type_display',
        },
        {
            title: 'Date',
            dataIndex: 'maintenance_date',
            key: 'maintenance_date',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Completed',
            dataIndex: 'completed_date',
            key: 'completed_date',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Cost',
            dataIndex: 'cost',
            key: 'cost',
            render: (text) => text ? `Rs. ${parseFloat(text).toLocaleString()}` : '-'
        },
        {
            title: 'Performed By',
            dataIndex: 'performed_by',
            key: 'performed_by',
            render: (text) => text || '-'
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Tag color={record.is_completed ? 'green' : 'orange'}>
                    {record.is_completed ? 'Completed' : 'In Progress'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Flex gap="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setCurrentMaintenance(record);
                            setMaintenanceDrawerVisible(true);
                        }}
                    />
                    <Popconfirm
                        title="Delete this record?"
                        onConfirm={() => handleDeleteMaintenance(record.id)}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Flex>
            )
        }
    ];

    const historyColumns = [
        {
            title: 'Date',
            dataIndex: 'history_date',
            key: 'history_date',
            render: (text) => dayjs(text).format('DD/MM/YYYY HH:mm')
        },
        {
            title: 'Status',
            dataIndex: 'status_display',
            key: 'status_display',
        },
        {
            title: 'Assigned To',
            dataIndex: 'assigned_to_name',
            key: 'assigned_to_name',
            render: (text) => text || '-'
        },
        {
            title: 'Location',
            dataIndex: 'location_name',
            key: 'location_name',
            render: (text) => text || '-'
        },
        {
            title: 'Modified By',
            dataIndex: 'history_user',
            key: 'history_user',
        }
    ];

    const tabItems = [
        {
            label: pendingImages.length > 0 ? `Images (${pendingImages.length} pending)` : 'Images',
            key: '0',
            children: (
                <div>
                    <Flex justify="end" style={{ marginBottom: 16 }}>
                        <Upload
                            beforeUpload={handleImageUpload}
                            showUploadList={false}
                            accept="image/*"
                            multiple
                        >
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                loading={uploading}
                            >
                                Upload Image
                            </Button>
                        </Upload>
                    </Flex>

                    {/* Pending Images Section - shown when creating new asset */}
                    {!id && pendingImages.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <div style={{
                                background: '#fffbe6',
                                border: '1px solid #ffe58f',
                                borderRadius: 8,
                                padding: 12,
                                marginBottom: 12
                            }}>
                                <Typography.Text type="warning">
                                    {pendingImages.length} image(s) will be uploaded when you save the asset.
                                </Typography.Text>
                            </div>
                            <Flex wrap="wrap" gap="middle">
                                {pendingImages.map((img) => (
                                    <div
                                        key={img.uid}
                                        style={{
                                            position: 'relative',
                                            border: '2px dashed #faad14',
                                            borderRadius: 8,
                                            padding: 4,
                                            background: '#fffbe6'
                                        }}
                                    >
                                        <img
                                            src={img.preview}
                                            alt={img.name}
                                            style={{
                                                width: 150,
                                                height: 150,
                                                objectFit: 'cover',
                                                borderRadius: 4
                                            }}
                                        />
                                        <Tag color="orange" style={{ position: 'absolute', top: 8, left: 8 }}>
                                            Pending
                                        </Tag>
                                        <Flex justify="center" gap="small" style={{ marginTop: 8 }}>
                                            <Button
                                                type="link"
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleRemovePendingImage(img.uid)}
                                                title="Remove"
                                            />
                                        </Flex>
                                        <div style={{ textAlign: 'center', fontSize: 11, color: '#666', marginTop: 4, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {img.name}
                                        </div>
                                    </div>
                                ))}
                            </Flex>
                        </div>
                    )}

                    {!id && pendingImages.length === 0 && (
                        <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
                            Add images here. They will be uploaded when you save the asset.
                        </div>
                    )}
                    {id && images.length === 0 && (
                        <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
                            No images uploaded yet
                        </div>
                    )}
                    <Image.PreviewGroup>
                        <Flex wrap="wrap" gap="middle">
                            {images.map((img) => (
                                <div
                                    key={img.id}
                                    style={{
                                        position: 'relative',
                                        border: img.is_primary ? '3px solid #1890ff' : '1px solid #ddd',
                                        borderRadius: 8,
                                        padding: 4,
                                        background: '#fff'
                                    }}
                                >
                                    <Image
                                        src={img.image_url}
                                        width={150}
                                        height={150}
                                        style={{ objectFit: 'cover', borderRadius: 4 }}
                                    />
                                    {img.is_primary && (
                                        <Tag color="blue" style={{ position: 'absolute', top: 8, left: 8 }}>
                                            Primary
                                        </Tag>
                                    )}
                                    <Flex justify="center" gap="small" style={{ marginTop: 8 }}>
                                        {!img.is_primary && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<StarOutlined />}
                                                onClick={() => handleSetPrimaryImage(img.id)}
                                                title="Set as Primary"
                                            />
                                        )}
                                        {img.is_primary && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<StarFilled style={{ color: '#faad14' }} />}
                                                disabled
                                                title="Primary Image"
                                            />
                                        )}
                                        <Popconfirm
                                            title="Delete this image?"
                                            onConfirm={() => handleDeleteImage(img.id)}
                                        >
                                            <Button
                                                type="link"
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                            />
                                        </Popconfirm>
                                    </Flex>
                                    {img.caption && (
                                        <div style={{ textAlign: 'center', fontSize: 12, color: '#666', marginTop: 4 }}>
                                            {img.caption}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </Flex>
                    </Image.PreviewGroup>
                </div>
            )
        },
        {
            label: 'Assignments',
            key: '1',
            children: (
                <div>
                    <Flex justify="end" style={{ marginBottom: 16 }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setCurrentAssignment(null);
                                setAssignmentDrawerVisible(true);
                            }}
                        >
                            Add Assignment
                        </Button>
                    </Flex>
                    <Table
                        columns={assignmentColumns}
                        dataSource={assignments}
                        rowKey="id"
                        size="small"
                        pagination={false}
                    />
                </div>
            )
        },
        {
            label: 'Maintenance',
            key: '2',
            children: (
                <div>
                    <Flex justify="end" style={{ marginBottom: 16 }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setCurrentMaintenance(null);
                                setMaintenanceDrawerVisible(true);
                            }}
                        >
                            Add Maintenance
                        </Button>
                    </Flex>
                    <Table
                        columns={maintenanceColumns}
                        dataSource={maintenanceRecords}
                        rowKey="id"
                        size="small"
                        pagination={false}
                    />
                </div>
            )
        },
        {
            label: 'History',
            key: '3',
            children: (
                <Table
                    columns={historyColumns}
                    dataSource={history}
                    rowKey="history_id"
                    size="small"
                    pagination={false}
                />
            )
        }
    ];

    return (
        <>
            <div className='custom-scroll-div' style={{ overflowY: "scroll", maxHeight: "90vh", padding: 10, paddingBottom: 30 }}>
                <Form form={form} onFinish={saveAsset} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} labelAlign="left">
                    <Flex justify='space-between' align='center' style={{ margin: "10px auto" }}>
                        <Typography.Title level={3}>{id ? 'Edit' : 'Create'} Asset</Typography.Title>
                        <Flex gap='middle'>
                            <Button onClick={() => navigate('/assets')}>Cancel</Button>
                            <Button
                                type="default"
                                onClick={() => form.validateFields().then(values => saveAsset(values, true))}
                                loading={loading}
                            >
                                Save and New
                            </Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Save
                            </Button>
                        </Flex>
                    </Flex>

                    <Row>
                        <Col span={12}>
                            <Card title="Asset Information" bordered={false}>
                                {id && (
                                    <Form.Item label="Asset Number" name="asset_number">
                                        <Input disabled style={{ fontWeight: 'bold', color: '#1890ff' }} />
                                    </Form.Item>
                                )}
                                <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter asset name' }]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Category" name="category">
                                    <Select
                                        showSearch
                                        placeholder="Select category"
                                        optionFilterProp="children"
                                        allowClear
                                    >
                                        {categories.map(cat => (
                                            <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Status" name="status" initialValue="AVAILABLE">
                                    <Select>
                                        <Option value="AVAILABLE">Available</Option>
                                        <Option value="ASSIGNED">Assigned</Option>
                                        <Option value="IN_MAINTENANCE">In Maintenance</Option>
                                        <Option value="RETIRED">Retired</Option>
                                        <Option value="LOST">Lost</Option>
                                        <Option value="DAMAGED">Damaged</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Serial Number" name="serial_number">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Model" name="model">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Manufacturer" name="manufacturer">
                                    <Input />
                                </Form.Item>
                            </Card>
                        </Col>
                        <Col span={12} style={{ paddingLeft: 10 }}>
                            <Card title="Barcode & QR Code" bordered={false}>
                                <Form.Item
                                    label={
                                        <span>
                                            Barcode
                                            {barcodeFromScanner && !id && (
                                                <Tag color="green" style={{ marginLeft: 8 }}>
                                                    <CheckCircleOutlined /> SCANNED
                                                </Tag>
                                            )}
                                        </span>
                                    }
                                    name="barcode"
                                >
                                    <Input
                                        placeholder="Enter existing barcode or generate new"
                                        disabled={barcodeFromScanner && !id}
                                        style={barcodeFromScanner && !id ? {
                                            backgroundColor: '#f6ffed',
                                            borderColor: '#52c41a',
                                            fontWeight: 'bold',
                                            color: '#389e0d'
                                        } : {}}
                                    />
                                </Form.Item>
                                {barcodeFromScanner && !id && (
                                    <div style={{
                                        marginTop: -16,
                                        marginBottom: 16,
                                        paddingLeft: '33.33%',
                                        color: '#52c41a',
                                        fontSize: 12
                                    }}>
                                        This barcode was scanned and will be used for the new asset.
                                    </div>
                                )}
                                <Form.Item label="Codes">
                                    <Flex gap="middle" align="flex-start">
                                        <div>
                                            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Barcode</div>
                                            {barcodeImage ? (
                                                <div style={{ background: 'white', padding: 10, border: '1px solid #ddd', display: 'inline-block' }}>
                                                    <img src={barcodeImage} alt="Barcode" style={{ maxWidth: 200 }} />
                                                </div>
                                            ) : (
                                                <span style={{ color: '#999' }}>Not generated</span>
                                            )}
                                        </div>
                                        <div>
                                            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>QR Code</div>
                                            {qrcodeImage ? (
                                                <div style={{ background: 'white', padding: 10, border: '1px solid #ddd', display: 'inline-block' }}>
                                                    <img src={qrcodeImage} alt="QR Code" style={{ width: 100, height: 100 }} />
                                                </div>
                                            ) : (
                                                <span style={{ color: '#999' }}>Not generated</span>
                                            )}
                                        </div>
                                    </Flex>
                                </Form.Item>
                                <Form.Item label="Actions" wrapperCol={{ offset: 8, span: 16 }}>
                                    <Flex gap="small">
                                        <Button
                                            icon={<BarcodeOutlined />}
                                            onClick={handleGenerateBarcode}
                                            disabled={!id}
                                        >
                                            Generate
                                        </Button>
                                        <Button
                                            icon={<PrinterOutlined />}
                                            onClick={handlePrintBarcode}
                                            disabled={!barcodeImage && !qrcodeImage}
                                        >
                                            Print
                                        </Button>
                                    </Flex>
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>

                    <Row style={{ marginTop: 10 }}>
                        <Col span={12}>
                            <Card title="Purchase Information" bordered={false}>
                                <Form.Item label="Purchase Date" name="purchase_date">
                                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item label="Purchase Price" name="purchase_price">
                                    <InputNumber
                                        prefix="Rs."
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={2}
                                    />
                                </Form.Item>
                                <Form.Item label="Warranty Expiry" name="warranty_expiry">
                                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item label="Vendor" name="vendor">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Invoice Number" name="invoice_number">
                                    <Input />
                                </Form.Item>
                            </Card>
                        </Col>
                        <Col span={12} style={{ paddingLeft: 10 }}>
                            <Card title="Location & Assignment" bordered={false}>
                                <Form.Item label="Location" name="location">
                                    <Select
                                        showSearch
                                        placeholder="Select location"
                                        optionFilterProp="children"
                                        allowClear
                                    >
                                        {locations.map(loc => (
                                            <Option key={loc.id} value={loc.id}>{loc.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Assigned To" name="assigned_to">
                                    <Select
                                        showSearch
                                        placeholder="Select employee"
                                        optionFilterProp="children"
                                        allowClear
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={employees.map(emp => {
                                            const userName = emp.user_name || '';
                                            const cleanName = userName.split(' ')
                                                .filter(part => part && part !== 'None')
                                                .join(' ')
                                                .trim();
                                            return {
                                                value: emp.id,
                                                label: `${cleanName || 'Unknown'} (${emp.employee_id || 'N/A'})`
                                            };
                                        })}
                                    />
                                </Form.Item>
                                <Form.Item label="Assigned Date" name="assigned_date">
                                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>

                    <Row style={{ marginTop: 10 }}>
                        <Col span={24}>
                            <Card title="Additional Information" bordered={false}>
                                <Form.Item label="Description" name="description" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
                                    <TextArea rows={3} />
                                </Form.Item>
                                <Form.Item label="Notes" name="notes" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
                                    <TextArea rows={3} />
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>

                    <Row style={{ marginTop: 10 }}>
                        <Col span={24}>
                            <Card bordered={false}>
                                <Tabs items={id ? tabItems : tabItems.filter(tab => tab.key === '0')} />
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>

            {/* Assignment Drawer */}
            <AssetAssignmentForm
                visible={assignmentDrawerVisible}
                onClose={() => {
                    setAssignmentDrawerVisible(false);
                    setCurrentAssignment(null);
                }}
                assetId={id}
                instance={currentAssignment}
                employees={employees}
                onSuccess={() => {
                    loadAsset();
                    setAssignmentDrawerVisible(false);
                    setCurrentAssignment(null);
                }}
            />

            {/* Maintenance Drawer */}
            <AssetMaintenanceForm
                visible={maintenanceDrawerVisible}
                onClose={() => {
                    setMaintenanceDrawerVisible(false);
                    setCurrentMaintenance(null);
                }}
                assetId={id}
                instance={currentMaintenance}
                onSuccess={() => {
                    loadAsset();
                    setMaintenanceDrawerVisible(false);
                    setCurrentMaintenance(null);
                }}
            />
        </>
    );
};

export default AssetForm;
